import React from 'react';
import { Card, CardContent, Box, Typography, Stack, LinearProgress } from '@mui/material';

/**
 * Reusable StatsCard component used across dashboards and management pages.
 */
export const StatsCard = ({
    title,
    label,
    value,
    subtitle,
    progress,
    icon,
    color = '#6366F1',
    showProgress = false,
    sx = {},
    cardSx = {},
    contentSx = {},
    displaySx = {},
    valueSx = {}
}) => {
    const displayTitle = title || label;
    const cardColor = color || '#6366F1';
    const bgGradient = typeof cardColor === 'string' && cardColor.includes('gradient')
        ? cardColor
        : `linear-gradient(135deg, ${cardColor} 0%, ${cardColor}dd 100%)`;

    return (
        <Card
            sx={{
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                height: '100%',
                background: bgGradient,
                color: '#FFFFFF',
                position: 'relative',
                overflow: 'hidden',
                containerType: 'inline-size',
                border: 'none',
                ...sx
            }}
        >
            <CardContent sx={{ position: 'relative', zIndex: 1, p: 3, ...cardSx, height: '100%' }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        height: '100%',
                        '@container (max-width: 250px)': {
                            flexDirection: 'column-reverse',
                            alignItems: 'flex-start'
                        },
                        ...contentSx
                    }}
                >
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Typography
                            variant="caption"
                            sx={{
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                fontWeight: '600',
                                opacity: 0.9,
                                display: 'block',
                                ...displaySx
                            }}
                        >
                            {displayTitle}
                        </Typography>
                        <Typography variant="h4" fontWeight="800" sx={{ mt: 0.5, ...valueSx }}>
                            {value ?? 0}
                        </Typography>
                        {subtitle && (
                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, mt: 0.5 }}>
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                    {icon && (
                        <Box sx={{ position: 'relative' }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '25%',
                                    left: '60%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '120px',
                                    height: '120px',
                                    borderRadius: '50%',
                                    background: 'rgba(255, 255, 255, 0.15)',
                                    pointerEvents: 'none'
                                }}
                            />
                            <Box
                                sx={{
                                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                                    p: 1.5,
                                    borderRadius: '12px',
                                    display: 'flex',
                                    backdropFilter: 'blur(4px)'
                                }}
                            >
                                {React.isValidElement(icon)
                                    ? React.cloneElement(icon, {
                                          sx: { color: '#FFFFFF', fontSize: 28, ...(icon.props?.sx || {}) }
                                      })
                                    : icon}
                            </Box>
                        </Box>
                    )}
                </Box>

                {(showProgress || progress !== undefined) && (
                    <Box sx={{ mt: 3 }}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: '600' }}>Completion</Typography>
                            <Typography variant="caption" sx={{ fontWeight: '700' }}>{progress}%</Typography>
                        </Stack>
                        <LinearProgress
                            variant="determinate"
                            value={progress || 0}
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: 'rgba(255, 255, 255, 0.2)',
                                '& .MuiLinearProgress-bar': {
                                    bgcolor: '#FFFFFF'
                                }
                            }}
                        />
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default StatsCard;
